import { Alert, Button, Flex, Skeleton, Space, Tag, Typography } from "antd";
import { useCallback, useEffect, useState } from "react";
import { TbArrowUpRight } from "react-icons/tb";
import striptags from "striptags";
import { useToken } from "../../hooks/useToken";
import { TInterest } from "../../models/interests.model";
import { TResponse } from "../../services/apiWorker";
import { getInterests } from "../../services/interests.service";
import { deepCopy } from "../../utils/deepCopy";
import { hexToRgba } from "../../utils/hexToRgba";
import { ModalPostNew } from "../timeline/ModalPostNew";

const { Text, Title } = Typography;

interface WikipediaSearchResult {
  ns: number;
  title: string;
  pageid: number;
  size: number;
  wordcount: number;
  snippet: string;
  timestamp: string;
  extract: string;
}

interface WikipediaSearchResponse {
  batchcomplete: string;
  continue?: {
    sroffset: number;
    continue: string;
  };
  query?: {
    searchinfo?: {
      totalhits: number;
      suggestion?: string;
      suggestionsnippet?: string;
    };
    search?: WikipediaSearchResult[];
  };
}

interface WikipediaPageImageResponse {
  batchcomplete?: string;
  query?: {
    pages?: {
      [pageId: string]: {
        pageid: number;
        ns: number;
        title: string;
        thumbnail?: {
          source: string;
          width: number;
          height: number;
        };
        pageimage?: string;
        extract?: string;
      };
    };
  };
}

function DashboardWikipedia() {
  const [interests, setInterests] = useState<TInterest[]>([]);
  const [selectedInterest, setSelectedInterest] = useState<TInterest>();
  const [wikipediaArticleInfo, setWikipediaArticleInfo] =
    useState<WikipediaSearchResult>();
  const [imageUrl, setImageUrl] = useState<string>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>();
  const [isAddingToTimeline, setAddingToTimeline] = useState<boolean>(false);

  const { screens, token } = useToken();

  useEffect(() => {
    setIsLoading(true);
    setError(undefined);

    getInterests({
      onSuccess: (data: TResponse<TInterest>) => {
        if (data.content && data.content.length > 0) {
          setInterests(data.content);
          fetchRandomFact(data.content);
        } else {
          setError("No interests found to fetch facts from.");
          setInterests([]);
          setIsLoading(false);
        }
      },
      onError: (err) => {
        console.error("Error fetching interests:", err);
        setError("Failed to load your interests.");
        setInterests([]);
        setIsLoading(false);
      },
    });
  }, []);

  const fetchRandomFact = useCallback(async (currentInterests: TInterest[]) => {
    if (!currentInterests || currentInterests.length === 0) {
      console.warn("Attempted to fetch fact without available interests.");
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(undefined);
    setWikipediaArticleInfo(undefined);
    setImageUrl(undefined);

    const randomIndex = Math.floor(Math.random() * currentInterests.length);
    const randomInterest = currentInterests[randomIndex];
    setSelectedInterest(randomInterest);

    console.log(`Workspaceing Wikipedia data for: ${randomInterest.name}`);
    const searchTerm = encodeURIComponent(randomInterest.name);
    const searchUrl = `https://en.wikipedia.org/w/api.php?action=query&list=search&srsearch=${searchTerm}&format=json&origin=*`;

    try {
      const searchResponse = await fetch(searchUrl);
      if (!searchResponse.ok) {
        throw new Error(
          `Wikipedia API Search Error: ${searchResponse.statusText} (${searchResponse.status})`
        );
      }
      const searchData: WikipediaSearchResponse = await searchResponse.json();
      const query = searchData.query;

      let foundArticle: WikipediaSearchResult | null = null;

      if (query && query.search && query.search.length > 0) {
        const searchResults = query.search;
        const randomFactIndex = Math.floor(
          Math.random() * searchResults.length
        );
        foundArticle = searchResults[randomFactIndex];
        setWikipediaArticleInfo(foundArticle);
      } else {
        console.log(
          `No results found on Wikipedia for "${randomInterest.name}"`
        );
        setWikipediaArticleInfo(undefined);
        setIsLoading(false);
        return;
      }

      if (foundArticle) {
        console.log(`Workspaceing image for pageid: ${foundArticle.pageid}`);
        const imageQueryUrl = `https://en.wikipedia.org/w/api.php?action=query&prop=pageimages&pageids=${foundArticle.pageid}&piprop=thumbnail&pithumbsize=100&format=json&origin=*`;

        const imageResponse = await fetch(imageQueryUrl);
        if (!imageResponse.ok) {
          console.error(
            `Wikipedia API Image Error: ${imageResponse.statusText} (${imageResponse.status})`
          );
        } else {
          const imageData: WikipediaPageImageResponse =
            await imageResponse.json();
          const pageId = foundArticle.pageid.toString();
          const imageUrlFromApi =
            imageData.query?.pages?.[pageId]?.thumbnail?.source;

          if (imageUrlFromApi) {
            setImageUrl(imageUrlFromApi);
            console.log("Image URL found:", imageUrlFromApi);
          } else {
            console.log("No thumbnail found for this page.");
            setImageUrl(undefined);
          }
        }

        const textQueryUrl = `https://en.wikipedia.org/w/api.php?action=query&prop=pageimages&pageids=${foundArticle.pageid}&piprop=thumbnail&pithumbsize=100&format=json&origin=*&prop=extracts&exintro&explaintext`;

        const textResponse = await fetch(textQueryUrl);
        if (!textResponse.ok) {
          console.error(
            `Wikipedia API Text Error: ${textResponse.statusText} (${textResponse.status})`
          );
        } else {
          const textData: WikipediaPageImageResponse =
            await textResponse.json();
          const pageId = foundArticle.pageid.toString();
          const textSnippet = textData.query?.pages?.[pageId]?.extract;

          if (textSnippet) {
            setWikipediaArticleInfo((prevState) => {
              const copy = deepCopy(prevState);

              if (copy) {
                copy.extract = textSnippet;
              }

              return copy;
            });
          } else {
            console.log("No extract found for this page.");
          }
        }
      }
    } catch (err: any) {
      console.error("Error fetching from Wikipedia:", err);
      setError(`Failed to fetch data from Wikipedia: ${err.message}`);
      setWikipediaArticleInfo(undefined);
      setImageUrl(undefined);
    } finally {
      setIsLoading(false);
    }
  }, []);

  return (
    <>
      {isAddingToTimeline && (
        <ModalPostNew
          onClose={() => {
            setAddingToTimeline(false);
          }}
          onCreate={() => {
            setAddingToTimeline(false);

            setIsLoading(true);
            setError(undefined);
            fetchRandomFact(interests);
          }}
          suggest={{
            title: wikipediaArticleInfo?.title || "",
            text: striptags(wikipediaArticleInfo?.extract || ""),
          }}
        />
      )}
      <Flex
        vertical
        style={{
          width: "100%",
          maxHeight: "100%",
          borderRadius: token.borderRadiusLG,
          // backgroundColor: token.colorBgContainer,
          backdropFilter: "blur(20px)",
          border: `2px solid ${token.colorBorderSecondary}`,
          padding: token.paddingMD,
        }}
      >
        {isLoading && (
          <Space direction="vertical" style={{ width: "100%" }} size="middle">
            <Skeleton active paragraph={false} />

            <Flex gap="middle" align="flex-start">
              <Skeleton.Avatar active />

              <Flex vertical style={{ flex: 1 }}>
                <Skeleton active />
              </Flex>
            </Flex>

            <Space>
              <Skeleton.Button active />
              <Skeleton.Button active />
            </Space>
          </Space>
        )}

        {!isLoading && error && (
          <Alert message="Error" description={error} type="error" showIcon />
        )}

        {!isLoading && !error && (
          <>
            {wikipediaArticleInfo && selectedInterest && (
              <Space
                direction="vertical"
                style={{ width: "100%" }}
                size="middle"
              >
                <Flex justify="space-between" align="center">
                  <Text
                    style={{
                      fontSize: token.fontSizeHeading4,
                      fontWeight: token.fontWeightStrong,
                      color: token.colorTextHeading,
                    }}
                  >
                    Wikipedia
                  </Text>
                  <Button
                    onClick={() => {
                      setAddingToTimeline(false);

                      setIsLoading(true);
                      setError(undefined);
                      fetchRandomFact(interests);
                    }}
                  >
                    Next
                  </Button>
                </Flex>

                <Flex vertical={!screens.md} gap="middle" align="flex-start">
                  {imageUrl && (
                    <img
                      src={imageUrl}
                      alt={`Thumbnail for ${wikipediaArticleInfo.title}`}
                      // preview={false}
                      style={{
                        borderRadius: token.borderRadiusLG,
                        objectFit: "cover",
                        height: "100px",
                        width: !screens.md ? "100%" : "100px",
                      }}
                    />
                  )}

                  <Flex vertical style={{ flex: 1 }}>
                    <Title level={5} style={{ margin: 0 }}>
                      {wikipediaArticleInfo.title}
                    </Title>

                    <div
                      style={{
                        marginTop: token.marginXS,
                        lineHeight: "1.5",
                        fontSize: token.fontSize,
                        color: token.colorTextSecondary,
                      }}
                    >
                      {striptags(wikipediaArticleInfo.snippet) + "..."}
                    </div>
                  </Flex>
                </Flex>

                <Flex wrap gap="10px" justify="space-between">
                  <Tag
                    style={{
                      color: selectedInterest.color,
                      backgroundColor:
                        hexToRgba(selectedInterest.color, 0.2) ||
                        selectedInterest.color,
                      border: `1px solid ${selectedInterest.color}`,
                      height: "fit-content",
                      margin: "auto 0",
                    }}
                  >
                    {selectedInterest.name}
                  </Tag>
                  <Flex
                    wrap={!screens.md}
                    justify={!screens.md ? "center" : "end"}
                    gap="10px"
                  >
                    <Button
                      type="link"
                      onClick={() => {
                        window.open(
                          `https://en.wikipedia.org/?curid=${wikipediaArticleInfo.pageid}`,
                          "_blank"
                        );
                      }}
                      icon={<TbArrowUpRight />}
                    >
                      View in Wikipedia
                    </Button>
                    <Button
                      type="primary"
                      onClick={() => {
                        setAddingToTimeline(true);
                      }}
                    >
                      Add to Timeline
                    </Button>
                  </Flex>
                </Flex>
              </Space>
            )}
            {!wikipediaArticleInfo && selectedInterest && (
              <Space direction="vertical" style={{ width: "100%" }}>
                <Text>Random fact about:</Text>
                <Tag
                  style={{
                    color: selectedInterest.color,
                    backgroundColor: "transparent",
                    border: `1px solid ${selectedInterest.color}`,
                  }}
                >
                  {selectedInterest.name}
                </Tag>
                <Text type="secondary">
                  No results found on Wikipedia for this interest.
                </Text>
              </Space>
            )}

            {interests.length == 0 && (
              <Text type="secondary">
                Could not load your interests to fetch facts.
              </Text>
            )}
          </>
        )}
      </Flex>
    </>
  );
}

export default DashboardWikipedia;
